import { useIntercom } from 'react-use-intercom'
import { generateZendeskToken } from '@/api'
import { useAuth } from '@/hooks/use-auth'
import { useTheme } from '@mui/material/styles'
import { useEffect, useMemo, useRef } from 'react'
import {
  getIntercomAlignment,
  getIntercomSideNavWidth,
  useIntercomAlignment,
  useIntercomSideNavWidth,
  type IntercomAlignment,
} from '@/hooks/use-intercom-messenger'
import { SupportChatButton } from '@/components/SupportChatButton'

/** Intercom impose un minimum de 20 px ; on aligne sur `theme.spacing(2)` du footer SideNav. */
function messengerEdgePaddingPx(themeSpacing2: string | number): number {
  const raw =
    typeof themeSpacing2 === 'number'
      ? themeSpacing2
      : Number.parseFloat(String(themeSpacing2))
  const px = Number.isFinite(raw) ? Math.round(raw) : 20
  return Math.max(20, px)
}

/** À gauche, la fenêtre passe à côté de la sidenav au lieu de la recouvrir. */
function messengerHorizontalPaddingPx(
  alignment: IntercomAlignment,
  sideNavWidth: number,
  edgePadding: number
): number {
  return alignment === 'left' ? sideNavWidth + edgePadding : edgePadding
}

const IntercomWrapper = ({ children }: { children: any }) => {
  const { user, isImpersonating } = useAuth()
  const theme = useTheme()
  const { boot, shutdown, update } = useIntercom()
  const alignment = useIntercomAlignment()
  const sideNavWidth = useIntercomSideNavWidth()
  const spacing2 = theme.spacing(2)
  const edgePadding = useMemo(
    () => messengerEdgePaddingPx(spacing2),
    [spacing2]
  )

  useEffect(() => {
    // En « en tant que », la messagerie serait celle du membre : le support y écrirait en son nom.
    if (!user || isImpersonating) {
      return shutdown()
    }
    if (user && user.email && user.uid) {
      // TODO - remove zendesk reference - we are actually using intercom here
      generateZendeskToken()
        .then(({ zendeskToken: userHash }) => {
          boot({
            email: user.email as string,
            userId: user.uid,
            name: user.profile?.firstName,
            userHash,
            createdAt: new Date().toISOString(),
            customAttributes: { product: 'app' },
            // `SupportChatButton` ouvre la messagerie : la bulle d'Intercom recouvrait les
            // boutons du coin (pagination, toasts, panneau des raccourcis).
            hideDefaultLauncher: true,
            alignment: getIntercomAlignment(),
            horizontalPadding: messengerHorizontalPaddingPx(
              getIntercomAlignment(),
              getIntercomSideNavWidth(),
              edgePadding
            ),
            verticalPadding: edgePadding,
          })
        })
        .catch((error: any) => {
          console.error('error starting intercom', error)
          shutdown()
        })
    }
    return () => shutdown()
  }, [boot, shutdown, user, isImpersonating, edgePadding])

  // Bouton déplacé de l'autre côté de l'écran, ou sidenav repliée ou absente : on recale la
  // fenêtre sans relancer la messagerie.
  const horizontalPadding = messengerHorizontalPaddingPx(
    alignment,
    sideNavWidth,
    edgePadding
  )
  const applied = useRef({ alignment, horizontalPadding })
  useEffect(() => {
    if (
      applied.current.alignment === alignment &&
      applied.current.horizontalPadding === horizontalPadding
    )
      return
    applied.current = { alignment, horizontalPadding }
    update({ alignment, horizontalPadding })
  }, [alignment, horizontalPadding, update])

  return (
    <>
      {children}
      {user?.email && user.uid && !isImpersonating && <SupportChatButton />}
    </>
  )
}

export default IntercomWrapper
